import { ShieldCheck, ShieldAlert, Users } from "lucide-react";
import type { Indicacao } from "@/modules/visitantes/services/visitantesService";
import { rotuloSexo } from "@/shared/lib/sexo";
import {
  ROTULO_CATEGORIA,
  ROTULO_PARENTESCO,
  categoriaDaIdade,
  rotuloAnos,
} from "@/modules/visitantes/utils/familia";
import {
  calcularIdade,
  formatarTelefone,
  rotuloEstadoCivil,
} from "@/modules/visitantes/utils/regras";

/**
 * Para o ministério responsável, antes de Adicionar: quem veio junto e se o líder registrou o
 * consentimento. Adicionar cria a pessoa e todos os familiares de uma vez; Não adicionar não cria
 * ninguém. Os dados dos familiares só aparecem aqui até a avaliação (depois ficam nos cadastros).
 */
export function ResumoPendente({ pendente }: { pendente: Indicacao }) {
  const familiares = pendente.familiares ?? [];
  return (
    <div className="mb-4 flex flex-col gap-3">
      <p
        className={`flex items-start gap-2 rounded-lg border px-4 py-3 text-sm ${
          pendente.consentimento
            ? "border-primary/30 bg-primary/5 text-text"
            : "border-warning/40 bg-warning/10 text-warning"
        }`}
      >
        {pendente.consentimento ? (
          <ShieldCheck size={18} className="mt-0.5 shrink-0 text-primary" aria-hidden="true" />
        ) : (
          <ShieldAlert size={18} className="mt-0.5 shrink-0" aria-hidden="true" />
        )}
        <span>
          {pendente.consentimento
            ? "O líder registrou que avisou a pessoa e ela concordou. O registro passa para o cadastro ao adicionar."
            : "Sem registro de consentimento (pendente anterior a esta regra). Depois de adicionar, registre o consentimento na ficha."}
        </span>
      </p>

      {familiares.length > 0 && (
        <section
          aria-label="Familiares que vieram junto"
          className="rounded-lg border border-border p-4"
        >
          <h2 className="flex items-center gap-2 text-sm font-bold text-text">
            <Users size={16} aria-hidden="true" /> Veio com {familiares.length}{" "}
            {familiares.length === 1 ? "familiar" : "familiares"}
          </h2>
          <ul className="mt-2 flex flex-col gap-1.5 text-sm text-text">
            {familiares.map((f, i) => {
              const idade = calcularIdade(f.nascimento);
              const cat = categoriaDaIdade(idade);
              const respondeu =
                !!f.igreja_situacao || !!f.cidade || !!f.preferencia_contato || !!f.pedido_oracao;
              return (
                <li key={`${f.nome}-${i}`}>
                  <span className="font-semibold">{f.nome}</span>{" "}
                  <span className="text-muted">
                    · {ROTULO_PARENTESCO[f.parentesco]}
                    {cat && idade !== null
                      ? ` · ${ROTULO_CATEGORIA[cat]} (${rotuloAnos(idade)})`
                      : ""}
                    {f.telefone ? ` · ${formatarTelefone(f.telefone)}` : ""}
                    {rotuloSexo(f.sexo) ? ` · ${rotuloSexo(f.sexo)}` : ""}
                    {rotuloEstadoCivil(f.estado_civil)
                      ? ` · ${rotuloEstadoCivil(f.estado_civil)}`
                      : ""}
                    {respondeu ? " · respondeu “Sobre a pessoa”" : ""}
                  </span>
                </li>
              );
            })}
          </ul>
          <p className="mt-2 text-xs text-muted">
            Adicionar cria todos de uma vez. Para ajustar os dados dos familiares, adicione e edite
            a ficha de cada um.
          </p>
        </section>
      )}
    </div>
  );
}
